import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/BrandingContext";
import { LogOut, ArrowRight, Crown, Lock, Eye, Clock } from "lucide-react";
import {
  getOperationsModulesWithAccess,
  getManagementModulesWithAccess,
} from "../../config/moduleConfig";
import FeatureShowcaseModal from "../../components/shared/FeatureShowcaseModal";
import { moduleConfig } from "../../config/moduleConfig";
/**
 * Get all modules combined (operations + management) with access status
 */
const getAllModulesWithAccess = (enabledModules) => {
  const operations = getOperationsModulesWithAccess(enabledModules);
  const management = getManagementModulesWithAccess(enabledModules);
  return [...operations, ...management];
};

/**
 * Module Card Component
 * Supports locked state for subscription-based access control
 * Supports coming soon state for modules in development
 */
const ModuleCard = ({ module, onClick, isLocked = false, onExplore }) => {
  const Icon = module.icon;

  // Coming Soon module card - special styling with preview
  if (module.isComingSoon) {
    return (
      <div className="group relative rounded-xl border border-purple-200 p-4 text-left overflow-hidden bg-gradient-to-br from-purple-50/50 to-white">
        {/* Coming Soon Badge - always visible */}
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-purple-100 text-purple-600 text-[10px] font-bold px-2 py-0.5 rounded-full z-30">
          <Clock size={10} />
          <span>Coming Soon</span>
        </div>

        {/* Subtle overlay */}
        <div className="absolute inset-0 bg-white/30 group-hover:bg-transparent transition-all duration-300 pointer-events-none z-10" />

        {/* Preview button - visible on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-purple-900/70 via-purple-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 z-20 flex flex-col items-center justify-end pb-4">
          <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onExplore?.(module);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white text-purple-700 rounded-lg text-xs font-semibold hover:bg-purple-50 transition-colors shadow-lg"
            >
              <Eye size={14} />
              Preview Features
            </button>
          </div>
        </div>

        {/* Header Row - Icon & Title */}
        <div className="flex items-start gap-3 mb-2 relative">
          <div
            className={`w-10 h-10 bg-gradient-to-br ${module.gradient} rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}
          >
            <Icon className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-gray-900 leading-tight">
              {module.name}
            </h3>
            <p className="text-xs text-gray-500 leading-snug mt-0.5">
              {module.description}
            </p>
          </div>
        </div>

        {/* Features */}
        <div className="flex flex-wrap gap-1 mt-3 relative">
          {module.features.map((feature, idx) => (
            <span
              key={idx}
              className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded font-medium"
            >
              {feature}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // Locked module card - elegant disabled state with hover reveal
  if (isLocked) {
    return (
      <div className="group relative rounded-xl border border-gray-200 p-4 text-left overflow-hidden bg-white">
        {/* Locked Badge - always visible, dim */}
        <div className="absolute top-2 right-2 flex items-center gap-1 text-gray-400 text-[10px] font-medium z-30">
          <Lock size={10} />
          <span>Locked</span>
        </div>

        {/* Grayscale overlay - fades on hover */}
        <div className="absolute inset-0 bg-gray-100/60 group-hover:bg-transparent transition-all duration-300 pointer-events-none z-10" />

        {/* Lock indicator - visible on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 z-20 flex flex-col items-center justify-end pb-4">
          <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onExplore?.(module);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white text-gray-900 rounded-lg text-xs font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              <Eye size={14} />
              Explore Features
            </button>
          </div>
        </div>

        {/* Header Row - Icon & Title (grayscale filter on icon) */}
        <div className="flex items-start gap-3 mb-2 relative">
          <div
            className={`w-10 h-10 bg-gradient-to-br ${module.gradient} rounded-lg flex items-center justify-center flex-shrink-0 grayscale group-hover:grayscale-0 transition-all duration-300`}
          >
            <Icon className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-gray-400 group-hover:text-gray-900 leading-tight transition-colors duration-300">
              {module.name}
            </h3>
            <p className="text-xs text-gray-400 leading-snug mt-0.5">
              {module.description}
            </p>
          </div>
        </div>

        {/* Features - muted */}
        <div className="flex flex-wrap gap-1 mt-3 relative">
          {module.features.map((feature, idx) => (
            <span
              key={idx}
              className="text-[10px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded font-medium"
            >
              {feature}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // Normal enabled module card
  return (
    <button
      onClick={onClick}
      className={`group relative bg-white rounded-xl border border-gray-200 p-4 text-left transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${module.bgHover}`}
    >
      {/* Premium Badge */}
      {module.isPremium && (
        <div className="absolute -top-2 -right-2 flex items-center gap-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">
          <Crown size={10} />
          <span>PRO</span>
        </div>
      )}

      {/* Header Row - Icon & Title */}
      <div className="flex items-start gap-3 mb-2">
        <div
          className={`w-10 h-10 bg-gradient-to-br ${module.gradient} rounded-lg flex items-center justify-center shadow group-hover:scale-105 transition-transform flex-shrink-0`}
        >
          <Icon className="w-5 h-5 text-white" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-gray-900 leading-tight">
            {module.name}
          </h3>
          <p className="text-xs text-gray-500 leading-snug mt-0.5">
            {module.description}
          </p>
        </div>
      </div>

      {/* Features */}
      <div className="flex flex-wrap gap-1 mt-3">
        {module.features.map((feature, idx) => (
          <span
            key={idx}
            className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium"
          >
            {feature}
          </span>
        ))}
      </div>

      {/* Hover Arrow */}
      <ArrowRight
        size={14}
        className="absolute bottom-3 right-3 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all"
      />
    </button>
  );
};

/**
 * Role Selection Page
 * Clean, modular interface for navigating to different application areas
 * Supports subscription-based module access control
 */
  const RoleSelectionPage = () => {
  const navigate = useNavigate();
  const { user, logout, hasRole, hasPermission, hasModuleAccess, isAuthenticated, getEnabledModules, loading } = useAuth();
  const branding = useBranding();
  // Maps moduleConfig ID → permission code (from permissionCodes.js ACCESS level)
  const permissionMap = {
    BACKOFFICE: "back_office",
    INVENTORY: "inventory",
    REPORTS: "reports",
    CRM: "crm",
    HRM: "hrm",
    QR_MENU: "qr_menu",
    POS: "pos",
    KDS: "kds",
    CAPTAIN_APP: "captain_app",
    INTEGRATIONS: "integrations",
    AI_INSIGHTS: "ai_insights",
  };

  // Modal state for locked module feature showcase
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated()) {
      navigate("/login");
    }
  }, [loading, isAuthenticated, navigate]);

  // Handler for exploring locked module features
  const handleExploreFeatures = (module) => {
    setSelectedModule(module);
    setShowFeatureModal(true);
  };

  const handleModuleClick = (module) => {
    // Don't navigate if module is locked
    if (module.isLocked) return;

    // Use basePath for module navigation
    // Special case for backoffice which goes to dashboard
    const path = module.id === 'BACKOFFICE' ? '/admin/dashboard' : module.basePath;
    navigate(path);
  };

  // Get only subscribed modules, filtered by role and branch subscription
  const allModules = Object.values(moduleConfig)
    .filter((module) => {
      if (user?.userType === "SUPER_USER") return true;

      if (!hasModuleAccess(module.id)) return false;

      const perm = permissionMap[module.id];
      if (!perm) return false;

      return hasPermission(perm);
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="w-full px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={branding.productLogoUrl}
              alt={branding.productName}
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="text-lg font-bold text-gray-900">{branding.productName}</h1>
              <p className="text-xs text-gray-500">{branding.productTagline}</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-gray-900">
                {user?.name || user?.username || "Guest"}
              </p>
              <p className="text-xs text-gray-500 capitalize">
                {user?.role === "both" ? "Manager" : user?.role || "Administrator"}
              </p>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium text-sm transition-colors"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 w-full px-4 py-6 overflow-auto">
        {/* Welcome Section */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            Welcome back, {user?.name?.split(" ")[0] || "there"}!
          </h2>
          <p className="text-gray-500">
            Select a module to get started
          </p>
        </div>

        {/* All Modules - Flat Grid */}
        {allModules.length > 0 && (
          <div className="mb-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {allModules.map((module) => (
                <ModuleCard
                  key={module.id}
                  module={module}
                  isLocked={module.isLocked}
                  onClick={() => handleModuleClick(module)}
                  onExplore={handleExploreFeatures}
                />
              ))}
            </div>
          </div>
        )}

      </main>

      {/* Feature Showcase Modal for Locked Modules */}
      <FeatureShowcaseModal
        isOpen={showFeatureModal}
        onClose={() => setShowFeatureModal(false)}
        module={selectedModule}
      />
    </div>
  );
};

export default RoleSelectionPage;
